/**
 * 발주서 → 매입 N건 자동 등록 모달.
 *
 *   ⭐ 품목 매칭 절차 (2026-06-18):
 *     발주서 품목(거래처용 명칭)을 그대로 매입에 올리지 않고, 먼저 기존 품명·품목
 *     (item_master)에 매칭한다. 매칭된 품목은 그 품목코드로 연결되어 신규 품목이
 *     생기지 않는다. 매칭이 없는 품목만 '신규 품목 등록'(AUTO 코드, 검토필요)으로 처리.
 *
 *   ⭐ 품목별 영업건 (v2.284.0 · 개발요청 #5 권애영 「한 발주서에 여러 영업건의 품목 · 품목별로 1:1 또는 1:다수 분할 연동」):
 *     품목마다 「영업건」 칸 — 기본은 대표 영업건(없으면 재고 보유). 발주서에 고른 영업건 중에서 고른다.
 *     한 품목을 여러 영업건으로 나눌 때는 [나누기] → 몫마다 수량·영업건(예: 10개 → 6개 A건 · 4개 B건).
 *     판매보고서가 확정된 영업건은 고를 수 없다(서버도 막는다) · 이미 연동한 발주서는 「그래도 다시 연동」을 체크해야 한다.
 *
 *   흐름: match(매칭·영업건) → 등록 → result
 *     - 신규 매입 INSERT (갱신 모드 없음 — 사용자 정책) · 나눈 품목은 몫마다 한 줄
 *     - 매입 발주서 PDF 는 영업건에 첨부하지 않음 (영업건 '발주서'=고객 발주서 전용)
 */

import { useEffect, useState } from 'react';
import {
  linkPoToPurchases,
  fetchPoItems,
  type PoDetail,
  type PoLinkAllocation,
  type PoLinkPurchaseResult,
  type PoLinkMatch,
  type PoLookupItem,
} from '../../lib/po-api';
import { PoItemSearchSelect } from '../../components/PoItemSearchSelect';

interface Props {
  open: boolean;
  order: PoDetail | null;
  onClose: () => void;
}

type Step = 'match' | 'result';

/** 품목 한 몫 — salesId null = 재고 보유 */
interface Part { salesId: string | null; qty: number }

/** 발주서 한 줄의 매칭 상태 */
interface LineState {
  sortOrder: number;
  poName: string;
  qty: number;
  unitPrice: number;
  query: string; // 검색창 텍스트
  picked: PoLookupItem | null; // 매칭된 기존 품목 (있으면 매칭됨)
  isNew: boolean; // 명시적으로 '신규 품목 등록' 선택
  /** 영업건 몫 — 하나면 그 영업건에 전부 · 여럿이면 나눠서 (v2.284.0) */
  parts: Part[];
}

const norm = (s: string) => s.trim().toLowerCase();
const STOCK = '__stock__';

/** 한 줄의 몫이 맞나 — 틀리면 까닭 */
export function partsProblem(ln: Pick<LineState, 'qty' | 'parts'>): string | null {
  const sum = ln.parts.reduce((a, p) => a + (Number(p.qty) || 0), 0);
  if (ln.parts.some((p) => !(Number(p.qty) > 0))) return '수량은 0보다 커야 해요';
  if (ln.parts.length > 1 && ln.parts.some((p) => !Number.isInteger(Number(p.qty)))) return '나눌 때는 정수로';
  if (Math.abs(sum - ln.qty) > 1e-6) return `합 ${sum} ≠ 발주 ${ln.qty}`;
  return null;
}

export function LinkPurchaseFromPoModal({ open, order, onClose }: Props) {
  const [step, setStep] = useState<Step>('match');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<PoLinkPurchaseResult | null>(null);

  const [loadingCand, setLoadingCand] = useState(false);
  const [candidates, setCandidates] = useState<PoLookupItem[]>([]);
  const [lines, setLines] = useState<LineState[]>([]);
  const [force, setForce] = useState(false);

  const linkedSales = order?.linkedSales ?? [];
  const primary = linkedSales[0]?.salesId ?? order?.linkedSalesId ?? null;

  useEffect(() => {
    if (!open || !order) return;
    setStep('match');
    setSubmitting(false);
    setErr(null);
    setResult(null);
    setForce(false);
    // 후보(기존 품명·품목 전체) 로드 + 이름 정확 일치 자동 매칭
    setLoadingCand(true);
    const defaultSales = order.linkedSales?.[0]?.salesId ?? order.linkedSalesId ?? null;
    setLines(
      order.items.map((it) => ({
        sortOrder: it.sortOrder,
        poName: it.itemName,
        qty: it.qty,
        unitPrice: it.unitPrice,
        query: it.itemName,
        picked: null,
        isNew: false,
        parts: [{ salesId: defaultSales, qty: it.qty }],
      })),
    );
    fetchPoItems()
      .then(({ items }) => {
        setCandidates(items);
        // 자동 매칭 — 품명이 (공백·대소문자 무시) 정확히 같은 품목
        setLines((prev) =>
          prev.map((ln) => {
            const exact = items.find((c) => norm(c.itemName) === norm(ln.poName));
            return exact ? { ...ln, picked: exact } : ln;
          }),
        );
      })
      .catch((e) => setErr('품목 목록 조회 실패: ' + (e as Error).message))
      .finally(() => setLoadingCand(false));
  }, [open, order]);

  if (!open || !order) return null;

  const matchedN = lines.filter((l) => l.picked).length;
  const newN = lines.filter((l) => !l.picked && l.isNew).length;
  const undecidedN = lines.filter((l) => !l.picked && !l.isNew).length;
  const badParts = lines.filter((l) => partsProblem(l) !== null).length;
  const finalizedIds = new Set(linkedSales.filter((s) => s.reportFinalized).map((s) => s.salesId));
  const usesFinalized = lines.some((l) => l.parts.some((p) => p.salesId && finalizedIds.has(p.salesId)));
  const alreadyLinked = (order.linkStatus?.purchaseCount ?? 0) > 0;
  const rowsToMake = lines.reduce((a, l) => a + l.parts.length, 0);

  function setLine(sortOrder: number, patch: Partial<LineState>) {
    setLines((prev) => prev.map((l) => (l.sortOrder === sortOrder ? { ...l, ...patch } : l)));
  }
  function setPart(sortOrder: number, idx: number, patch: Partial<Part>) {
    setLines((prev) => prev.map((l) => (l.sortOrder === sortOrder
      ? { ...l, parts: l.parts.map((p, i) => (i === idx ? { ...p, ...patch } : p)) }
      : l)));
  }

  async function submit() {
    if (undecidedN > 0) {
      setErr(`아직 정하지 않은 품목이 ${undecidedN}건 있습니다. 기존 품목에 매칭하거나 '신규 품목 등록'을 선택하세요.`);
      return;
    }
    if (badParts > 0) {
      setErr(`영업건별로 나눈 수량이 맞지 않는 품목이 ${badParts}건 있습니다.`);
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const matches: PoLinkMatch[] = lines
        .filter((l) => l.picked)
        .map((l) => ({ sortOrder: l.sortOrder, itemCode: l.picked!.itemCode }));
      const allocations: PoLinkAllocation[] = lines.map((l) => ({
        sortOrder: l.sortOrder,
        parts: l.parts.map((p) => ({ salesId: p.salesId, qty: Number(p.qty) })),
      }));
      const r = await linkPoToPurchases(order!.id, matches, allocations, force);
      setResult(r);
      setStep('result');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const salesLabel = (sid: string | null): string => {
    if (!sid) return '재고 보유(영업건 없음)';
    const s = linkedSales.find((x) => x.salesId === sid);
    const tail = [s?.endUser, s?.projectName].filter(Boolean).join(' · ');
    return `${sid}${sid === primary ? ' [대표]' : ''}${tail ? ` — ${tail}` : ''}`;
  };

  // 함수로 부른다(안에서 만든 컴포넌트는 그릴 때마다 새로 붙어 선택 상자가 다시 만들어진다)
  const salesSelect = (value: string | null, onChange: (v: string | null) => void) => (
    <select
      className="ui-input w-full min-w-0 text-xs"
      value={value ?? STOCK}
      onChange={(e) => onChange(e.target.value === STOCK ? null : e.target.value)}
      aria-label="영업건"
    >
      {linkedSales.map((s) => (
        <option key={s.salesId} value={s.salesId} disabled={s.reportFinalized}>
          {salesLabel(s.salesId)}{s.reportFinalized ? ' (판보 확정 — 매입 못 붙임)' : ''}
        </option>
      ))}
      <option value={STOCK}>{salesLabel(null)}</option>
    </select>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto p-4">
      <div
        className="bg-white rounded shadow-xl w-full max-w-5xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="ui-modal-title">매입 연동 — 품목 매칭 · 영업건</h2>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
          >
            ×
          </button>
        </div>

        <div className="p-5">
          {err && (
            <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm whitespace-pre-wrap">
              {err}
            </div>
          )}

          {step === 'match' && (
            <div>
              {/* 이미 연동한 발주서 — 두 번 누르면 매입이 두 벌 생긴다 */}
              {alreadyLinked && (
                <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-900 rounded text-sm">
                  ⚠ 이 발주서는 이미 매입 연동했습니다(매입 {order.linkStatus.purchaseCount}줄 · {order.linkStatus.lastLinkedAt?.slice(0, 16).replace('T', ' ') ?? ''}).
                  다시 등록하면 매입이 <b>또</b> 생깁니다 — 매입관리에서 먼저 확인하세요.
                  <label className="mt-1 flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} /> 그래도 다시 연동(매입을 새로 더 만든다)
                  </label>
                </div>
              )}

              {/* 발주서 요약 */}
              <div className="mb-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs bg-slate-50 border border-slate-200 rounded p-3">
                <Info label="발주서 번호" value={order.poNo} />
                <Info label="매입처" value={order.recipientSupplier || '-'} />
                <Info label="프로젝트명" value={order.projectName || '-'} />
                <Info label="발주일자" value={order.poDate} />
                <Info
                  label="영업건 연결"
                  value={linkedSales.length
                    ? linkedSales.map((s) => `${s.salesId}${s.salesId === primary ? '(대표)' : ''}${s.reportFinalized ? '·판보확정' : ''}`).join(', ')
                    : '(없음 — 재고 보유 매입)'}
                />
                <Info label="품목" value={`${order.items.length}건`} />
              </div>

              <p className="text-xs text-slate-600 mb-2">
                발주서 품목(거래처용 명칭)을 <b>기존 품명·품목</b>에 매칭하세요. 매칭하면 새 품목이
                생기지 않고 기존 품목으로 매입이 연결됩니다. 목록에 없는 품목만{' '}
                <b>[신규 품목 등록]</b>으로 처리합니다.
                {linkedSales.length > 1 && <> 품목마다 <b>영업건</b>을 고르고, 한 품목이 여러 영업건이면 <b>[나누기]</b>로 수량을 나눕니다.</>}
              </p>

              {/* 매칭 표 */}
              <div className="border border-slate-200 rounded overflow-visible">
                <div className="grid grid-cols-[1fr_1.3fr_1.2fr] gap-2 px-3 py-1.5 bg-slate-100 text-xs font-semibold text-slate-600">
                  <div>발주서 품목 (거래처용)</div>
                  <div>→ 내부 품명·품목 매칭</div>
                  <div>영업건</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {lines.map((ln) => {
                    const problem = partsProblem(ln);
                    const canSplit = Number.isInteger(ln.qty) && ln.qty >= 2;
                    return (
                    <div key={ln.sortOrder} className="grid grid-cols-[1fr_1.3fr_1.2fr] gap-2 px-3 py-2 items-start">
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-slate-800 break-words">{ln.poName || '(품명 없음)'}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {ln.qty}개 × {ln.unitPrice.toLocaleString()}원
                        </div>
                      </div>
                      <div className="min-w-0">
                        {ln.picked ? (
                          <div className="flex items-start gap-1.5">
                            <div className="flex-1 min-w-0 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded">
                              <div className="text-xs font-medium text-emerald-800 break-words">
                                ✓ {ln.picked.itemName}
                                {ln.picked.itemGroup && (
                                  <span className="ml-1 text-[11px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-600">
                                    {ln.picked.itemGroup}
                                  </span>
                                )}
                              </div>
                              {ln.picked.spec && (
                                <div className="text-[11px] text-emerald-600 mt-0.5 line-clamp-2 whitespace-pre-wrap">
                                  {ln.picked.spec}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setLine(ln.sortOrder, { picked: null, isNew: false })}
                              className="btn btn-ghost btn-sm shrink-0"
                            >
                              변경
                            </button>
                          </div>
                        ) : ln.isNew ? (
                          <div className="flex items-start gap-1.5">
                            <div className="flex-1 min-w-0 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                              ✍ 신규 품목 등록 <span className="text-[11px] text-amber-600">(검토필요로 등록)</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setLine(ln.sortOrder, { isNew: false })}
                              className="btn btn-ghost btn-sm shrink-0"
                            >
                              매칭
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <PoItemSearchSelect
                              value={ln.query}
                              candidates={candidates}
                              supplierFiltered={false}
                              supplierLabel=""
                              loading={loadingCand}
                              onChange={(v) => setLine(ln.sortOrder, { query: v })}
                              onPick={(it) => setLine(ln.sortOrder, { picked: it })}
                            />
                            <button
                              type="button"
                              onClick={() => setLine(ln.sortOrder, { isNew: true })}
                              className="btn btn-warning btn-sm"
                            >
                              ✍ 신규 품목으로 등록
                            </button>
                          </div>
                        )}
                      </div>
                      {/* 영업건 — 한 몫이면 고르기만 · 나누면 몫마다 수량·영업건 (v2.284.0) */}
                      <div className="min-w-0 space-y-1">
                        {ln.parts.length === 1 ? (
                          <div className="flex items-center gap-1.5">
                            <div className="flex-1 min-w-0">
                              {salesSelect(ln.parts[0]!.salesId, (v) => setPart(ln.sortOrder, 0, { salesId: v }))}
                            </div>
                            {canSplit && (
                              <button type="button" className="btn btn-outline btn-sm shrink-0" title="이 품목을 여러 영업건으로 수량을 나눠 연동"
                                onClick={() => {
                                  const first = Math.ceil(ln.qty / 2);
                                  const other = linkedSales.find((s) => s.salesId !== ln.parts[0]!.salesId && !s.reportFinalized)?.salesId ?? null;
                                  setLine(ln.sortOrder, { parts: [{ salesId: ln.parts[0]!.salesId, qty: first }, { salesId: other, qty: ln.qty - first }] });
                                }}>
                                나누기
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            {ln.parts.map((p, i) => (
                              <div key={i} className="flex items-center gap-1">
                                <input type="number" min={1} step={1} className="ui-input w-16 text-xs text-right" value={p.qty}
                                  aria-label="나눈 수량"
                                  onChange={(e) => setPart(ln.sortOrder, i, { qty: Number(e.target.value) })} />
                                <span className="text-[11px] text-slate-500">개 →</span>
                                <div className="flex-1 min-w-0">
                                  {salesSelect(p.salesId, (v) => setPart(ln.sortOrder, i, { salesId: v }))}
                                </div>
                                <button type="button" className="text-slate-500 hover:text-red-600 shrink-0" aria-label="이 몫 빼기" title="이 몫 빼기"
                                  onClick={() => setLine(ln.sortOrder, { parts: ln.parts.length > 2 ? ln.parts.filter((_, k) => k !== i) : [{ salesId: ln.parts[i === 0 ? 1 : 0]!.salesId, qty: ln.qty }] })}>
                                  ×
                                </button>
                              </div>
                            ))}
                            <div className="flex items-center gap-2 text-[11px]">
                              <button type="button" className="text-sky-700 hover:underline"
                                onClick={() => setLine(ln.sortOrder, { parts: [...ln.parts, { salesId: null, qty: 1 }] })}>
                                + 한 몫 더
                              </button>
                              <button type="button" className="text-slate-500 hover:underline"
                                onClick={() => setLine(ln.sortOrder, { parts: [{ salesId: ln.parts[0]!.salesId, qty: ln.qty }] })}>
                                나누기 취소
                              </button>
                              <span className={problem ? 'text-rose-600 font-medium' : 'text-emerald-700'}>
                                {problem ?? `합 ${ln.qty} ✓`}
                              </span>
                            </div>
                          </>
                        )}
                        {ln.parts.length === 1 && problem && <div className="text-[11px] text-rose-600">{problem}</div>}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-2 flex items-center gap-3 text-xs">
                <span className="text-emerald-700">매칭 {matchedN}</span>
                <span className="text-amber-700">신규 {newN}</span>
                {undecidedN > 0 && <span className="text-rose-600 font-medium">미정 {undecidedN}</span>}
                {badParts > 0 && <span className="text-rose-600 font-medium">수량 안 맞음 {badParts}</span>}
                {rowsToMake !== lines.length && <span className="text-slate-600">매입 {rowsToMake}줄로 등록</span>}
              </div>
              {usesFinalized && (
                <div className="mt-2 text-xs text-rose-600">판매보고서가 확정된 영업건에는 매입을 붙일 수 없습니다 — 다른 영업건을 고르세요.</div>
              )}
              <div className="mt-2 text-xs text-slate-500">
                · 매입 발주서 PDF 는 영업건에 첨부되지 않습니다 (영업건 '발주서'=고객 발주서 전용).
              </div>
            </div>
          )}

          {step === 'result' && result && (
            <div>
              <div className="text-center py-4">
                <div className="text-4xl mb-2">✅</div>
                <div className="text-base font-bold text-slate-800 mb-1">
                  매입 {result.purchaseCount ?? result.itemCount}건 등록 완료
                </div>
                <div className="text-xs text-slate-500">
                  매입 ID: {result.purchaseIds.map((id) => `#${id}`).join(', ')}
                </div>
              </div>
              <div className="text-xs text-slate-600 bg-slate-50 rounded p-3 space-y-1">
                <div>
                  · 기존 품목 매칭 <b>{result.matchedCount}건</b> · 신규 품목 등록{' '}
                  <b>{result.newItemCount}건</b>
                  {result.newItemCount > 0 && (
                    <span className="text-amber-600"> (시스템관리에서 검토 필요)</span>
                  )}
                </div>
                {(result.bySales ?? []).length > 0 ? (
                  (result.bySales ?? []).map((b) => (
                    <div key={b.salesId ?? 'stock'}>
                      · {b.salesId ? <span className="font-mono">{b.salesId}</span> : '재고 보유(영업건 없음)'} — 매입 {b.rows}줄 · 수량 {b.qty}
                    </div>
                  ))
                ) : result.linkedSalesId ? (
                  <div>· 영업건 연결: <span className="font-mono">{result.linkedSalesId}</span></div>
                ) : (
                  <div>· 영업건 연결 없음 — 재고 보유 매입으로 등록됨</div>
                )}
                <div>· 매입관리 화면에서 입고/출고/세금계산서 등 4단계 진행하세요.</div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {step === 'match' && (
            <button
              type="button"
              onClick={submit}
              disabled={submitting || lines.length === 0 || undecidedN > 0 || badParts > 0 || usesFinalized || (alreadyLinked && !force)}
              className="btn btn-primary"
              title={undecidedN > 0 ? `미정 품목 ${undecidedN}건을 먼저 처리하세요`
                : badParts > 0 ? '나눈 수량을 맞춰 주세요'
                : alreadyLinked && !force ? '이미 연동한 발주서 — 「그래도 다시 연동」을 체크하세요' : ''}
            >
              {submitting ? '등록 중...' : `매입 ${rowsToMake}건 등록`}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            {step === 'result' ? '완료' : '취소'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1.5">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-800 font-medium break-all">{value}</span>
    </div>
  );
}
